import { apiService } from '../../../components/api/apiService';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/data-stores';
const LOG_PREFIX = '[DataStores]';

export interface DataStoreSummary {
  id: string;
  name: string;
  connectorKey: string;
  category?: string;
  description?: string;
  status?: string;
  environment?: string;
  tags?: string[];
  createdAt?: string;
  updatedAt?: string;
}

function logDataStores(
  level: 'app' | 'warning' | 'error',
  event: string,
  extra?: Record<string, unknown>
): void {
  const payload = extra ? { event, ...extra } : { event };
  if (level === 'error') {
    console.error(LOG_PREFIX, '[error]', payload);
    return;
  }
  if (level === 'warning') {
    console.warn(LOG_PREFIX, '[warning]', payload);
    return;
  }
  console.info(LOG_PREFIX, '[app]', payload);
}

function serviceErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as {
    response?: { data?: { error?: { message?: string }; message?: string } };
    message?: string;
  };
  return axiosErr.response?.data?.error?.message || axiosErr.response?.data?.message || axiosErr.message || fallback;
}

function asList(data: unknown): DataStoreSummary[] {
  const record = data && typeof data === 'object' ? (data as { datastores?: unknown; dataStores?: unknown }) : {};
  const raw = Array.isArray(record.datastores)
    ? record.datastores
    : Array.isArray(record.dataStores)
      ? record.dataStores
      : Array.isArray(data)
        ? data
        : [];
  return raw
    .filter((item) => item && typeof item === 'object')
    .map((item) => {
      const row = item as Record<string, unknown>;
      return {
        id: String(row.id || ''),
        name: String(row.name || ''),
        connectorKey: String(row.connectorKey || row.connector_key || ''),
        category: String(row.category || ''),
        description: String(row.description || ''),
        status: String(row.status || ''),
        environment: String(row.environment || ''),
        tags: Array.isArray(row.tags) ? row.tags.map((tag) => String(tag)) : [],
      };
    })
    .filter((row) => row.id && row.name);
}

export async function listDataStores(): Promise<DataStoreSummary[]> {
  logDataStores('app', 'list_datastores');
  try {
    const data = await apiService.get(BASE, {}, CONNECTION);
    const rows = asList(data);
    logDataStores('app', 'list_datastores_ok', { count: rows.length });
    return rows;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to load connections');
    logDataStores('error', 'list_datastores_failed', { message });
    throw new Error(message);
  }
}

export async function testDataStore(
  body: Record<string, unknown>
): Promise<{ success: boolean; message: string }> {
  logDataStores('app', 'test_datastore', { connectorKey: body.connectorKey });
  try {
    const data = (await apiService.post(`${BASE}/test`, body, CONNECTION)) as {
      success?: boolean;
      message?: string;
    };
    const success = Boolean(data?.success);
    const message = data?.message || (success ? 'Connection successful' : 'Connection failed.');
    logDataStores(success ? 'app' : 'warning', 'test_datastore_done', {
      connectorKey: body.connectorKey,
      success,
    });
    return { success, message };
  } catch (err) {
    const message = serviceErrorMessage(err, 'Error connecting to the API server.');
    logDataStores('error', 'test_datastore_failed', { message, connectorKey: body.connectorKey });
    return { success: false, message };
  }
}

export async function discoverDataStore(storeId: string): Promise<unknown> {
  logDataStores('app', 'discover_datastore', { id: storeId });
  try {
    const data = await apiService.get(`${BASE}/${encodeURIComponent(storeId)}/discover`, {}, CONNECTION);
    const record = data && typeof data === 'object' ? (data as { nodes?: unknown; Nodes?: unknown }) : {};
    const count = Array.isArray(record.nodes) ? record.nodes.length : Array.isArray(record.Nodes) ? record.Nodes.length : 0;
    logDataStores('app', 'discover_datastore_ok', { id: storeId, count });
    return data;
  } catch (err) {
    const message = serviceErrorMessage(err, 'Unable to load SAP HANA schemas');
    logDataStores('error', 'discover_datastore_failed', { message, id: storeId });
    throw new Error(message);
  }
}

export async function createDataStore(body: Record<string, unknown>): Promise<DataStoreSummary> {
  logDataStores('app', 'create_datastore', { connectorKey: body.connectorKey, name: body.name });
  try {
    const data = (await apiService.post(BASE, body, CONNECTION)) as Record<string, unknown>;
    const created: DataStoreSummary = {
      id: String(data?.id || ''),
      name: String(data?.name || body.name || ''),
      connectorKey: String(data?.connectorKey || body.connectorKey || ''),
      category: String(data?.category || ''),
      description: String(data?.description || ''),
      status: String(data?.status || 'Active'),
      environment: String(data?.environment || body.environment || ''),
      tags: Array.isArray(data?.tags) ? data.tags.map((tag) => String(tag)) : [],
    };
    if (!created.id) {
      logDataStores('error', 'create_datastore_missing_id', { name: created.name });
      throw new Error('Connection was saved without an id');
    }
    logDataStores('app', 'create_datastore_ok', { id: created.id, connectorKey: created.connectorKey });
    return created;
  } catch (err) {
    if (err instanceof Error && err.message === 'Connection was saved without an id') throw err;
    const message = serviceErrorMessage(err, 'Error creating connection');
    logDataStores('error', 'create_datastore_failed', { message, connectorKey: body.connectorKey });
    throw new Error(message);
  }
}
